import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { AlertCircle, BotMessageSquare, Check, ChevronLeft, CircleStop, History, Loader2, RotateCcw, Send, SquarePen, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "../ui/sheet";
import { Textarea } from "../ui/textarea";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg, fmtDateTime } from "../../lib/api";
import { useRoleModules } from "../../lib/roleModules";
import { ArtifactChart } from "./ArtifactChart";
import { ArtifactTable } from "./ArtifactTable";
import { streamAssistantMessage } from "./useAssistantStream";

/**
 * Phase 171 — AI Assistant: floating bubble (bottom-right) + right-side panel, shared by every portal.
 * The backend decides what the user may see; this component only renders the stream.
 */

// Compact markdown for chat bubbles (headings capped at h3 by the prompt; tables are rendered as artifacts, not markdown).
const md = {
  h1: ({ children }) => <p className="font-heading font-semibold text-sm mb-1">{children}</p>,
  h2: ({ children }) => <p className="font-heading font-semibold text-sm mb-1">{children}</p>,
  h3: ({ children }) => <p className="font-heading font-semibold text-sm mb-1">{children}</p>,
  p: ({ children }) => <p className="text-sm leading-relaxed mb-1.5 last:mb-0">{children}</p>,
  ul: ({ children }) => <ul className="list-disc pl-4 mb-1.5 space-y-0.5 text-sm leading-relaxed">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-4 mb-1.5 space-y-0.5 text-sm leading-relaxed">{children}</ol>,
  li: ({ children }) => <li>{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  a: ({ href, children }) => <a href={href} className="text-primary underline underline-offset-2" target="_blank" rel="noreferrer">{children}</a>,
  code: ({ children }) => <code className="font-mono text-[12px] bg-muted px-1 py-0.5 rounded">{children}</code>,
  table: ({ children }) => <div className="overflow-x-auto mb-1.5 rounded-md border"><table className="text-xs w-full">{children}</table></div>,
  th: ({ children }) => <th className="text-left font-semibold px-2 py-1 border-b bg-muted/50">{children}</th>,
  td: ({ children }) => <td className="px-2 py-1 border-b last:border-0 align-top">{children}</td>,
  hr: () => <hr className="my-2 border-border" />,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-primary/40 pl-3 text-sm text-muted-foreground mb-1.5">{children}</blockquote>,
};

const storageKey = (code, uid) => `mc_assistant_conv_${code}_${uid}`;

const ToolChips = ({ tools, testId }) => {
  if (!tools?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid={testId}>
      {tools.map((t, i) => (
        <span key={i} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] ${t.running ? "text-muted-foreground" : t.ok ? "text-[hsl(var(--success))] border-[hsl(var(--success))]/30" : "text-[hsl(var(--warning))] border-[hsl(var(--warning))]/40"}`}
          title={t.error || t.label}>
          {t.running ? <Loader2 className="h-3 w-3 animate-spin" /> : t.ok ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
          {t.label}{t.running ? "…" : ""}
        </span>
      ))}
    </div>
  );
};

const AssistantMessage = ({ m, code, onRetry }) => {
  const tables = (m.artifacts || []).filter((a) => a.kind === "table");
  const charts = (m.artifacts || []).filter((a) => a.kind === "chart");
  return (
    <div className="flex gap-2 items-start" data-testid={`assistant-message-${m.id}`} data-role="assistant">
      <div className="h-7 w-7 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5" aria-hidden="true">
        <BotMessageSquare className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1 space-y-2">
        <div className="rounded-2xl rounded-tl-md border bg-card shadow-sm px-3.5 py-2.5">
          <ToolChips tools={m.tools} testId={`assistant-tools-${m.id}`} />
          {m.content ? (
            <div className="break-words" data-testid={`assistant-content-${m.id}`}>
              <ReactMarkdown remarkPlugins={[remarkGfm]} components={md}>{m.content}</ReactMarkdown>
            </div>
          ) : m.streaming ? (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground py-0.5" data-testid="assistant-thinking">
              <span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-pulse" /><span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-pulse [animation-delay:150ms]" /><span className="h-1.5 w-1.5 rounded-full bg-primary/60 animate-pulse [animation-delay:300ms]" />
              <span className="ml-1">Thinking</span>
            </div>
          ) : null}
          {m.error && (
            <div className="mt-1.5 flex items-start gap-2 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/5 px-2.5 py-2 text-xs" data-testid="assistant-error">
              <AlertCircle className="h-3.5 w-3.5 text-[hsl(var(--warning))] shrink-0 mt-0.5" />
              <div className="flex-1">
                <p>{m.error}</p>
                {onRetry && <button type="button" onClick={onRetry} className="mt-1 inline-flex items-center gap-1 text-primary font-medium hover:underline" data-testid="assistant-retry"><RotateCcw className="h-3 w-3" /> Try again</button>}
              </div>
            </div>
          )}
        </div>
        {tables.map((a, i) => <ArtifactTable key={`t${i}`} artifact={a} code={code} testId={`artifact-table-${m.id}-${i}`} />)}
        {charts.map((a, i) => <ArtifactChart key={`c${i}`} artifact={a} testId={`artifact-chart-${m.id}-${i}`} />)}
      </div>
    </div>
  );
};

const UserMessage = ({ m }) => (
  <div className="flex justify-end" data-testid={`assistant-message-${m.id}`} data-role="user">
    <div className="max-w-[85%] rounded-2xl rounded-tr-md bg-primary text-primary-foreground px-3.5 py-2 text-sm whitespace-pre-wrap break-words shadow-sm">{m.content}</div>
  </div>
);

export const AssistantWidget = ({ code, fabClassName = "bottom-4 sm:bottom-6" }) => {
  const { auth } = useAuth();
  const { allowed } = useRoleModules(code);
  const user = auth?.user;
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("chat");           // chat | history
  const [meta, setMeta] = useState(null);             // /suggestions payload
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(() => (user && code ? localStorage.getItem(storageKey(code, user.id)) : null));
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [lastQuestion, setLastQuestion] = useState("");
  const abortRef = useRef(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const openedOnce = useRef(false);

  const enabled = !!code && !!user && user.role !== "super_admin" && allowed("ai_assistant") && !user.must_change_password;

  const persistActive = useCallback((id) => {
    setActiveId(id);
    if (user && code) {
      if (id) localStorage.setItem(storageKey(code, user.id), id);
      else localStorage.removeItem(storageKey(code, user.id));
    }
  }, [code, user]);

  const loadConversations = useCallback(async () => {
    try {
      const r = await api.get(`/t/${code}/assistant/conversations`);
      setConversations(r.data.items || []);
      return r.data.items || [];
    } catch (e) {
      toast.error(errMsg(e));
      return [];
    }
  }, [code]);

  const loadMessages = useCallback(async (id) => {
    if (!id) { setMessages([]); return; }
    setLoadingMsgs(true);
    try {
      const r = await api.get(`/t/${code}/assistant/conversations/${id}`);
      setMessages((r.data.messages || []).map((m) => ({ id: m.id, role: m.role, content: m.content, artifacts: m.artifacts || [], tools: [], error: m.error || null })));
    } catch (e) {
      if (e?.response?.status === 404) { persistActive(null); setMessages([]); } else toast.error(errMsg(e));
    } finally {
      setLoadingMsgs(false);
    }
  }, [code, persistActive]);

  // every open: refresh scope / suggestions (territory or module changes apply immediately); first open also resumes the last chat
  useEffect(() => {
    if (!open || !enabled) return;
    api.get(`/t/${code}/assistant/suggestions`).then((r) => setMeta(r.data)).catch(() => setMeta(null));
    if (openedOnce.current) return;
    openedOnce.current = true;
    loadConversations().then((items) => {
      if (activeId && !items.some((c) => c.id === activeId)) persistActive(null);
      else if (activeId) loadMessages(activeId);
    });
  }, [open, enabled, code, activeId, loadConversations, loadMessages, persistActive]);

  useEffect(() => {
    if (open && view === "chat") bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, open, view]);

  useEffect(() => {
    if (open && view === "chat" && !streaming) setTimeout(() => inputRef.current?.focus(), 150);
  }, [open, view, streaming, activeId]);

  const newChat = () => {
    if (streaming) return;
    persistActive(null);
    setMessages([]);
    setView("chat");
    setInput("");
  };

  const openConversation = async (id) => {
    if (streaming) return;
    persistActive(id);
    setView("chat");
    await loadMessages(id);
  };

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/t/${code}/assistant/conversations/${id}`);
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (id === activeId) { persistActive(null); setMessages([]); }
      setConfirmDelete(null);
      toast.success("Conversation deleted");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const stop = () => { abortRef.current?.abort(); };

  const send = async (textArg) => {
    const text = (textArg ?? input).trim();
    if (!text || streaming) return;
    setInput("");
    setLastQuestion(text);
    setStreaming(true);
    let convId = activeId;
    try {
      if (!convId) {
        const r = await api.post(`/t/${code}/assistant/conversations`, {});
        convId = r.data.id;
        persistActive(convId);
      }
    } catch (e) {
      setStreaming(false);
      toast.error(errMsg(e));
      return;
    }
    const tmpUser = `u-${Date.now()}`;
    const tmpAsst = `a-${Date.now()}`;
    setMessages((ms) => [...ms, { id: tmpUser, role: "user", content: text }, { id: tmpAsst, role: "assistant", content: "", artifacts: [], tools: [], streaming: true, error: null }]);
    const asstId = { current: tmpAsst };
    const patch = (fn) => setMessages((ms) => ms.map((m) => (m.id === asstId.current ? fn(m) : m)));
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      await streamAssistantMessage({
        code, conversationId: convId, text, signal: ctrl.signal,
        onEvent: (event, data) => {
          if (event === "meta") {
            setMessages((ms) => ms.map((m) => (m.id === tmpUser ? { ...m, id: data.user_message_id || m.id } : m)));
            if (data.title) setConversations((cs) => cs.map((c) => (c.id === convId ? { ...c, title: data.title } : c)));
          } else if (event === "token") {
            patch((m) => ({ ...m, content: (m.content || "") + (data.text || "") }));
          } else if (event === "tool_start") {
            patch((m) => ({ ...m, tools: [...(m.tools || []), { name: data.name, label: data.label, running: true }] }));
          } else if (event === "tool_done") {
            patch((m) => {
              const tools = [...(m.tools || [])];
              const idx = tools.findIndex((t) => t.name === data.name && t.running);
              const done = { name: data.name, label: data.label, running: false, ok: data.ok, error: data.error };
              if (idx >= 0) tools[idx] = done; else tools.push(done);
              return { ...m, tools };
            });
          } else if (event === "artifact") {
            patch((m) => ({ ...m, artifacts: [...(m.artifacts || []), data] }));
          } else if (event === "done") {
            patch((m) => ({ ...m, id: data.message_id || m.id, content: data.content || m.content, streaming: false,
              artifacts: (m.artifacts || []).map((a) => ({ ...a, message_id: data.message_id || a.message_id })) }));
            if (data.message_id) asstId.current = data.message_id;
          } else if (event === "error") {
            patch((m) => ({ ...m, streaming: false, error: data.message || "Something went wrong", content: m.content || data.partial || "" }));
          }
        },
      });
    } catch (e) {
      const aborted = e?.name === "AbortError";
      patch((m) => ({ ...m, streaming: false, error: aborted ? (m.content ? null : "Stopped.") : (m.error || e.message || "The assistant could not reply.") }));
    } finally {
      abortRef.current = null;
      setStreaming(false);
      // ensure any still-running chips are closed
      setMessages((ms) => ms.map((m) => (m.tools?.some((t) => t.running) ? { ...m, tools: m.tools.map((t) => (t.running ? { ...t, running: false, ok: false } : t)) } : m)));
      loadConversations();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const greeting = useMemo(() => {
    const first = (user?.name || "").split(" ")[0];
    return first ? `Namaste, ${first}` : "Namaste";
  }, [user]);

  if (!enabled) return null;

  const title = meta?.role === "citizen" ? "Citizen Assistant" : "Municipal Assistant";
  const subtitle = auth?.municipality?.short_name || auth?.municipality?.name || "MunicipalConnect";

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} aria-label="Open AI assistant" data-testid="assistant-fab"
        className={`fixed right-4 sm:right-6 ${fabClassName} z-[60] h-14 w-14 rounded-full bg-primary text-primary-foreground shadow-[var(--shadow-md)] flex items-center justify-center transition-transform duration-200 hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${open ? "pointer-events-none opacity-0" : "opacity-100"}`}>
        <BotMessageSquare className="h-6 w-6" aria-hidden="true" />
      </button>

      <Sheet open={open} onOpenChange={(o) => { if (!o && streaming) stop(); setOpen(o); }}>
        <SheetContent side="right" className="p-0 w-full sm:max-w-md md:max-w-lg flex flex-col gap-0 bg-background [&>button]:top-3.5 [&>button]:right-3.5" data-testid="assistant-panel">
          {/* header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b bg-card pr-12">
            {view === "history" ? (
              <Button variant="ghost" size="icon" className="h-8 w-8 -ml-1" onClick={() => setView("chat")} aria-label="Back to chat" data-testid="assistant-history-back"><ChevronLeft className="h-4 w-4" /></Button>
            ) : (
              <div className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0" aria-hidden="true"><BotMessageSquare className="h-5 w-5" /></div>
            )}
            <div className="min-w-0 flex-1">
              <SheetTitle className="font-heading text-sm font-semibold leading-tight truncate">{view === "history" ? "Past conversations" : title}</SheetTitle>
              <SheetDescription className="text-[11px] text-muted-foreground truncate">{view === "history" ? `${conversations.length} saved` : `${subtitle} · English / हिंदी`}</SheetDescription>
            </div>
            {view === "chat" && (
              <>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { loadConversations(); setView("history"); }} aria-label="Conversation history" title="History" data-testid="assistant-history-toggle"><History className="h-4 w-4" /></Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={newChat} disabled={streaming} aria-label="New chat" title="New chat" data-testid="assistant-new-chat"><SquarePen className="h-4 w-4" /></Button>
              </>
            )}
          </div>

          {view === "history" ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-1.5" data-testid="assistant-history-list">
              {!conversations.length && <p className="text-sm text-muted-foreground text-center py-10" data-testid="assistant-history-empty">No saved conversations yet.</p>}
              {conversations.map((c) => (
                <div key={c.id} className={`group flex items-start gap-2 rounded-xl border px-3 py-2 ${c.id === activeId ? "border-primary/50 bg-primary/5" : "bg-card hover:bg-muted/40"}`}>
                  <button type="button" onClick={() => openConversation(c.id)} className="flex-1 text-left min-w-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded" data-testid={`assistant-history-item-${c.id}`}>
                    <p className="text-sm font-medium truncate">{c.title || "New chat"}</p>
                    <p className="text-[11px] text-muted-foreground truncate">{c.last_preview || "—"}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDateTime(c.updated_at)} · {c.message_count || 0} messages</p>
                  </button>
                  {confirmDelete === c.id ? (
                    <div className="flex flex-col gap-1 shrink-0">
                      <Button size="sm" variant="destructive" className="h-7 text-xs" onClick={() => deleteConversation(c.id)} data-testid={`assistant-history-delete-confirm-${c.id}`}>Delete</Button>
                      <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    </div>
                  ) : (
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => setConfirmDelete(c.id)} aria-label="Delete conversation" data-testid={`assistant-history-delete-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <>
              {/* messages */}
              <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="assistant-messages">
                {loadingMsgs && <div className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading conversation…</div>}
                {!loadingMsgs && !messages.length && (
                  <div className="space-y-4" data-testid="assistant-empty-state">
                    <div className="rounded-2xl border bg-card shadow-sm p-4">
                      <p className="font-heading font-semibold text-base">{greeting}</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {meta?.role === "citizen"
                          ? "Ask me anything about your property — dues, payments, receipts, assessment, visits, grievances or applications. Type in English or Hindi."
                          : "Ask about any property, ward, mohalla, collector, collections or performance — and request reports on demand. Type in English or Hindi."}
                      </p>
                      {meta?.scope && <p className="text-[11px] text-muted-foreground mt-2" data-testid="assistant-scope">Data limited to: {meta.scope}</p>}
                      {meta && meta.configured === false && <p className="text-xs text-[hsl(var(--warning))] mt-2">The AI service is not configured on this server.</p>}
                    </div>
                    {!!meta?.suggestions?.length && (
                      <div>
                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium mb-1.5 px-1">Try asking</p>
                        <div className="flex flex-wrap gap-1.5">
                          {meta.suggestions.map((s, i) => (
                            <button key={i} type="button" onClick={() => send(s.text)} data-testid={`assistant-suggestion-${i}`}
                              className="text-left text-xs rounded-full border bg-card px-3 py-1.5 hover:bg-primary/5 hover:border-primary/40 transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
                              {s.text}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {messages.map((m, i) => m.role === "user"
                  ? <UserMessage key={m.id} m={m} />
                  : <AssistantMessage key={m.id} m={m} code={code} onRetry={m.error && i === messages.length - 1 && lastQuestion ? () => { setMessages((ms) => ms.slice(0, -2)); send(lastQuestion); } : null} />)}
                {!streaming && !loadingMsgs && messages.length > 0 && messages[messages.length - 1].role === "user" && (
                  <div className="flex items-center justify-end gap-2 text-xs text-muted-foreground -mt-2" data-testid="assistant-unanswered">
                    <span>No answer was recorded for this question.</span>
                    <button type="button" onClick={() => send(messages[messages.length - 1].content)} className="inline-flex items-center gap-1 text-primary font-medium hover:underline" data-testid="assistant-ask-again">
                      <RotateCcw className="h-3 w-3" /> Ask again
                    </button>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* composer */}
              <div className="border-t bg-card p-3">
                <div className="flex items-end gap-2">
                  <Textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown} rows={1} maxLength={4000}
                    placeholder={meta?.role === "citizen" ? "e.g. What are my dues? / मेरा बकाया कितना है?" : "e.g. Ward-wise collection this month / आज का कलेक्शन?"}
                    className="min-h-[44px] max-h-32 resize-none text-sm" disabled={streaming} data-testid="assistant-input" aria-label="Message the assistant" />
                  {streaming ? (
                    <Button variant="outline" size="icon" className="h-11 w-11 shrink-0" onClick={stop} aria-label="Stop" title="Stop" data-testid="assistant-stop"><CircleStop className="h-5 w-5" /></Button>
                  ) : (
                    <Button size="icon" className="h-11 w-11 shrink-0" onClick={() => send()} disabled={!input.trim()} aria-label="Send" title="Send" data-testid="assistant-send"><Send className="h-5 w-5" /></Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground mt-1.5 px-0.5">Live data on every question · read-only · only data you are authorised to see · Enter to send, Shift+Enter for a new line</p>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
};
